
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import WordCloud from '@/word-cloud/WordCloud.vue';
import { WordCloud as WC } from '@/word-cloud/interfaces';
import { get } from 'lodash';
import Icon from '@/common/icon/Icon.vue';
import { Theme } from '@/side-bar/side-bar-config.interface';
import PollCountdown from './PollCountdown.vue';

import { TRANSITION_DURATION } from '../../constants';

const POST_NOTIFICATION_TIMEOUT = 3 * 1000;

export default Vue.extend({

    components: {
        WordCloud,
        Icon,
        PollCountdown
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        pollId: {
            type: String,
            required: true,
        },
        question: {
            type: String,
            required: true,
        },
        countdownEndTimestamp: {
            type: Number,
            default: -1,
        },
        countdownDuration: {
            type: Number,
            default: -1,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        theme: {
            type: Object as PropType<Theme>,
            default: () => ({})
        },
        characterLimit: {
            type: Number,
            default: 0
        },
        canSubmitAnswer: {
            type: Boolean,
            default: true
        },
        allowVoting: {
            type: Boolean,
            default: true
        },
        allowMultiWords: {
            type: Boolean,
            default: false
        },
        showResults: {
            type: Boolean,
            default: false
        },
        results: {
            type: Array,
            default: () => ([])
        },
        wordCloudConfig: {
            type: Object as PropType<WC.Config>,
            default: () => ({})
        },
        expanded: {
            type: Boolean,
            default: false
        },
        preventExpand: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            answer: '',
            showMessage: false,
            message: '',
            sending: false,
            messageTimeout: 0,
        };
    },

    computed: {
        disableSubmit(): boolean {
            return this.sending || !this.answer || !this.answer.length || !this.canSubmitAnswer;
        },

        words(): WC.Word[] {
            return this.results.map((result:unknown) => (
                {
                    text: get(result, 'text') as string,
                    votes: get(result, 'votes', 0) as number
                }
            )).filter((word: WC.Word) => !!word.text && word.votes > 0);
        },

        showExpandButton(): boolean {
            return !this.preventExpand && this.words.length > 0 && !this.expanded;
        }
    },

    watch: {
        answer() {
            if (!this.canSubmitAnswer) {
                this.displayNewMessage(this.labels.youCannotSubmitMore as string);
                return;
            }
            if (this.hasWhiteSpace(this.answer)) {
                if (this.allowMultiWords) {
                    this.displayNewMessage(this.labels.multipleWordAnswerTooltip as string);
                } else {
                    this.answer = this.answer.replace(/\s/g, '');
                }
            }
        }
    },

    beforeDestroy() {
        clearTimeout(this.messageTimeout);
    },

    methods: {
        sendAnswer():void {
            this.sending = true;
            this.api.customAction({
                pollId: this.pollId,
                answer: this.answer,
                onSuccess: (successMessage: string) => this.onSendingSuccess(successMessage),
                onError: (errorMessage: string) => this.onSendingError(errorMessage)
            }, 'sideBarPolls');
        },

        onSendingSuccess(successMessage:string): void {
            this.sending = false;
            this.answer = '';
            this.displayNewMessage(successMessage || this.labels.thankYouForAnswer as string);
        },

        onSendingError(errorMessage:string):void {
            this.sending = false;
            this.displayNewMessage(errorMessage || this.labels.anErrorOccurred as string);
        },

        displayNewMessage(message: string): void {
            if (!message) {
                return;
            }
            clearTimeout(this.messageTimeout);
            this.message = message;
            this.showMessage = true;
            this.messageTimeout = setTimeout(() => this.showMessage = false, POST_NOTIFICATION_TIMEOUT);
        },

        hasWhiteSpace(s:string) {
            return s.indexOf(' ') >= 0;
        },

        expandSidebar():void {
            this.api.expandSidebar('sidebar.wordcloud');
        },

        collapseSidebar():void {
            const sidebar = this.$root?.$el?.querySelector?.('.smvp-side-bar');
            sidebar?.classList?.add?.('smvp-side-bar-narrowing');

            setTimeout(() => {
                this.api.collapseSidebar('sidebar.wordcloud');
                sidebar?.classList?.remove?.('smvp-side-bar-narrowing');
            }, TRANSITION_DURATION - 25);
        }
    }
});
